import { useState, useRef, useCallback } from "react";

interface FileDropZoneProps {
  value: File | { fileName: string; size?: number } | null;
  onChange: (file: File | null) => void;
  accept?: string;
  label?: string;
  required?: boolean;
  id?: string;
  describedBy?: string;
  invalid?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "📄";
  if (["doc", "docx"].includes(ext ?? "")) return "📝";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext ?? "")) return "🖼️";
  return "📎";
}

export function FileDropZone({ value, onChange, accept, label = "Upload file", required, id, describedBy, invalid }: FileDropZoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File | null) => {
      onChange(file);
    },
    [onChange]
  );

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };
  const onDragLeave = () => setDragging(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0] ?? null;
    if (file) handleFile(file);
  };
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0] ?? null);
  };

  if (value) {
    const fileName = value instanceof File ? value.name : value.fileName;
    const fileSize = value instanceof File ? value.size : (value.size ?? 0);
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-surface-overlay border border-status-active-border text-sm">
        <span className="text-xl leading-none">{getFileIcon(fileName)}</span>
        <div className="flex-1 min-w-0">
          <p className="text-content-primary font-medium truncate">{fileName}</p>
          {fileSize > 0 && <p className="text-content-muted text-xs mt-0.5">{formatBytes(fileSize)}</p>}
        </div>
        <button
          type="button"
          onClick={() => {
            handleFile(null);
            if (inputRef.current) inputRef.current.value = "";
          }}
          className="shrink-0 min-h-11 min-w-11 p-2 rounded-lg text-content-muted hover:text-status-rejected-text hover:bg-status-rejected-bg transition-colors"
          aria-label="Remove file"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <label
      htmlFor={id}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`focus-within:ring-2 focus-within:ring-accent relative flex flex-col items-center justify-center gap-2 px-6 py-8 rounded-xl border-2 border-dashed cursor-pointer transition-all select-none ${
        dragging
          ? "border-accent bg-accent-muted"
          : "border-border-default bg-surface-overlay hover:border-accent"
      }`}
    >
      <div className={`p-3 rounded-full transition-colors ${dragging ? "bg-accent-muted" : "bg-surface-muted"}`}>
        <svg
          className={`w-6 h-6 transition-colors ${dragging ? "text-accent" : "text-content-muted"}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
          />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-sm text-content-primary font-medium">
          {dragging ? "Drop to upload" : `${label}: drag and drop or browse`}
        </p>
        {accept && (
          <p className="text-xs text-content-muted mt-1">
            {accept.split(",").join(", ")} · Max 50 MB
          </p>
        )}
      </div>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        required={required && !value}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        onChange={onInputChange}
        className="sr-only"
      />
    </label>
  );
}
